import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldErrors, FieldMapping, isStreamType, Rule, StreamType } from "./fieldMappingTypes";
import { formClasses } from "../../common/tailwindClassnames";
import ValueMappingRows, { MapEntry } from "./ValueMappingRows";

type FieldMappingFormProps = Rule & {
    onChange: (next: Rule) => void;
    errors?: FieldErrors;
};

function mapToEntries(map: Record<string, string> | undefined): MapEntry[] {
    if (!map) return [];
    return Object.entries(map);
}

function entriesToMap(entries: MapEntry[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of entries) {
        if (key.trim() !== "") out[key] = value;
    }
    return out;
}

const FieldMappingForm = ({ streamType, mapping, onChange, errors }: FieldMappingFormProps) => {
    const { t } = useTranslation();

    // Local state for entries so that empty draft rows survive re-renders.
    const [entries, setEntries] = useState<MapEntry[]>(() => mapToEntries(mapping.map));

    const initialHasMappingData =
        (mapping.provider_field ?? "").trim() !== "" || entries.length > 0;

    const [addValueMapping, setAddValueMapping] = useState<boolean>(initialHasMappingData);

    // When the prop-driven mapping changes externally (e.g., loading an existing rule),
    // resync entries. But if the local entries already represent the parent's map (modulo
    // empty draft rows the user added), keep them — otherwise clicking "+ Add row" would
    // wipe the blank row right after we added it.
    useEffect(() => {
        const next = mapToEntries(mapping.map);
        setEntries((current) => {
            const currentFiltered = entriesToMap(current);
            const parentMap = mapping.map ?? {};
            const currentKeys = Object.keys(currentFiltered);
            const parentKeys = Object.keys(parentMap);
            const alreadyInSync =
                currentKeys.length === parentKeys.length &&
                currentKeys.every((key) => currentFiltered[key] === parentMap[key]);
            return alreadyInSync ? current : next;
        });
        // intentionally only re-sync on mapping.map reference change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapping.map]);

    const propagateEntries = (next: MapEntry[]) => {
        setEntries(next);
        onChange({ streamType, mapping: { ...mapping, map: entriesToMap(next) } });
    };

    const handleStreamTypeChange = (next: StreamType) => {
        // Both ev and obv support optional value mappings, so we just hand over the
        // existing mapping; the user controls the section via the checkbox.
        onChange({ streamType: next, mapping });
    };

    const handleToggleAddValueMapping = (checked: boolean) => {
        setAddValueMapping(checked);
        if (checked) {
            if (entries.length === 0) setEntries([["", ""]]);
        } else {
            setEntries([]);
            onChange({ streamType, mapping: { ...mapping, provider_field: "", map: {} } });
        }
    };

    const updateMapping = (patch: Partial<FieldMapping>) => {
        onChange({ streamType, mapping: { ...mapping, ...patch } });
    };

    const isEvents = streamType === "ev";
    const destinationFieldPlaceholder = isEvents ? "event_type" : "provider_key";
    const defaultValuePlaceholder = isEvents ? "wildlife_sighting" : "your_key";
    const sourceFieldPlaceholder = isEvents ? "event_details__species" : "additional__type";

    return (
        <div className="flex flex-col gap-4 p-4" data-testid="FieldMappingForm-container">
            <div className={formClasses.group}>
                <label className={formClasses.label} htmlFor="stream-type-select">
                    {t("Stream Type")}
                </label>
                <select
                    id="stream-type-select"
                    data-testid="FieldMappingForm-stream-type"
                    className={formClasses.select}
                    value={streamType}
                    onChange={(event) => {
                        // The <select> only lists `ev` and `obv`, but isStreamType keeps
                        // the cast honest if the DOM is ever tampered with.
                        if (isStreamType(event.target.value)) handleStreamTypeChange(event.target.value);
                    }}
                >
                    <option value="ev">{t("Events")}</option>
                    <option value="obv">{t("Observations")}</option>
                </select>
            </div>

            <div className={formClasses.group}>
                <label className={formClasses.label} htmlFor="destination-field-input">
                    {t("Field to change")}
                </label>
                <input
                    id="destination-field-input"
                    data-testid="FieldMappingForm-destination-field"
                    className={formClasses.input}
                    type="text"
                    placeholder={destinationFieldPlaceholder}
                    value={mapping.destination_field ?? ""}
                    onChange={(event) => updateMapping({ destination_field: event.target.value })}
                />
                {errors?.destination_field && <p className={formClasses.error}>{errors.destination_field}</p>}
            </div>

            <div className={formClasses.group}>
                <label className={formClasses.label} htmlFor="default-value-input">
                    {t("Default value")}
                </label>
                <input
                    id="default-value-input"
                    data-testid="FieldMappingForm-default-value"
                    className={formClasses.input}
                    type="text"
                    placeholder={defaultValuePlaceholder}
                    value={mapping.default ?? ""}
                    onChange={(event) => updateMapping({ default: event.target.value })}
                />
                {errors?.default && <p className={formClasses.error}>{errors.default}</p>}
                <p className="mt-1 text-xs text-secondary-gray">{t("Used when no mapping matches.")}</p>
            </div>

            <div className="flex flex-col gap-2">
                <label className="flex flex-row items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        className="accent-dark-green w-4 h-4"
                        data-testid="FieldMappingForm-add-value-mapping"
                        checked={addValueMapping}
                        onChange={(event) => handleToggleAddValueMapping(event.target.checked)}
                    />
                    <span className="text-sm text-off-black">{t("Add value mapping")}</span>
                </label>

                {addValueMapping && (
                    <div className="bg-light-gray rounded p-3 flex flex-col gap-3" data-testid="FieldMappingForm-mapping-section">
                        <div className={formClasses.group + " mb-0"}>
                            <label className={formClasses.label} htmlFor="provider-field-input">
                                {t("Source field for mapping")}
                            </label>
                            <input
                                id="provider-field-input"
                                data-testid="FieldMappingForm-provider-field"
                                className={formClasses.input}
                                type="text"
                                placeholder={sourceFieldPlaceholder}
                                value={mapping.provider_field ?? ""}
                                onChange={(event) => updateMapping({ provider_field: event.target.value })}
                            />
                            {errors?.provider_field && <p className={formClasses.error}>{errors.provider_field}</p>}
                        </div>

                        <ValueMappingRows entries={entries} onChange={propagateEntries} />
                        {errors?.map && <p className={formClasses.error}>{errors.map}</p>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FieldMappingForm;
